import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import { Close, GppBadOutlined } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import BackdropOverlay from "../common/BackdropOverlay";
import {
    DIALOG_CANCEL_BUTTON_SX,
    DIALOG_PAPER_RADIUS,
    LOBBY_EXIT_BUTTON_SX,
} from "../common/dialogActionButtonSx";
import { colors } from "../../theme/colors";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();
const CODE_LENGTH = 6;

const DIALOG_FADE_IN = {
    "0%": { opacity: 0 },
    "100%": { opacity: 1 },
};

const DIALOG_PAPER_SX = {
    "@keyframes dialogFadeIn": { ...DIALOG_FADE_IN },
    position: "relative",
    width: { xs: "100%", sm: "30rem" },
    maxWidth: "30rem",
    overflow: "hidden",
    background: `
        linear-gradient(
            165deg,
            rgba(18,14,14,0.98) 0%,
            rgba(10,10,12,0.99) 48%,
            rgba(8,8,10,1) 100%
        )
    `,
    borderRadius: DIALOG_PAPER_RADIUS,
    border: `1px solid ${colors.border.default}`,
    boxShadow: "0 28px 64px rgba(0, 0, 0, 0.55)",
    animation: "dialogFadeIn 0.2s ease-in-out",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "12%",
        right: "12%",
        height: "1px",
        background: `linear-gradient(90deg, transparent, ${alpha(colors.accent.main, 0.55)}, transparent)`,
        pointerEvents: "none",
    },
};

function SixDigitCodeInput({ value, onChange, disabled, autoFocus = false }) {
    const inputRefs = useRef([]);

    useEffect(() => {
        if (!autoFocus) return;
        const timer = setTimeout(() => inputRefs.current[0]?.focus(), 120);
        return () => clearTimeout(timer);
    }, [autoFocus]);

    const digits = value.padEnd(CODE_LENGTH, " ").slice(0, CODE_LENGTH).split("");

    const updateAtIndex = (index, char) => {
        const next = digits.map((digit, i) => (i === index ? char : digit.trim())).join("");
        onChange(next.replace(/\s/g, "").slice(0, CODE_LENGTH));
    };

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                gap: "0.55rem",
                mt: "1.35rem",
            }}
        >
            {digits.map((digit, index) => (
                <Box
                    key={`disable-code-digit-${index}`}
                    component="input"
                    ref={(node) => {
                        inputRefs.current[index] = node;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    maxLength={1}
                    value={digit.trim()}
                    disabled={disabled}
                    aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
                    onChange={(event) => {
                        const char = event.target.value.replace(/\D/g, "").slice(-1);
                        updateAtIndex(index, char);
                        if (char && index < CODE_LENGTH - 1) {
                            inputRefs.current[index + 1]?.focus();
                        }
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Backspace" && !digit.trim() && index > 0) {
                            inputRefs.current[index - 1]?.focus();
                        }
                    }}
                    onPaste={(event) => {
                        event.preventDefault();
                        const pasted = event.clipboardData
                            .getData("text")
                            .replace(/\D/g, "")
                            .slice(0, CODE_LENGTH);
                        if (pasted) {
                            onChange(pasted);
                            const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
                            inputRefs.current[focusIndex]?.focus();
                        }
                    }}
                    sx={{
                        width: "2.75rem",
                        height: "3.35rem",
                        textAlign: "center",
                        fontSize: "1.3rem",
                        fontWeight: 600,
                        fontFamily: "inherit",
                        color: colors.text.primary,
                        background: "rgba(255,255,255,0.03)",
                        border: `1px solid ${colors.border.default}`,
                        borderRadius: "10px",
                        outline: "none",
                        transition: "border-color 0.15s ease-in-out, background 0.15s ease-in-out",
                        "&:focus": {
                            borderColor: colors.primary.main,
                            background: "rgba(255,255,255,0.05)",
                        },
                        "&:disabled": {
                            opacity: 0.55,
                            cursor: "not-allowed",
                        },
                    }}
                />
            ))}
        </Box>
    );
}

SixDigitCodeInput.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    autoFocus: PropTypes.bool,
};

export default function TwoFactorAuthDisableDialog({ open, onClose, onDisabled }) {
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const resetState = useCallback(() => {
        setCode("");
        setLoading(false);
        setError(null);
    }, []);

    const handleClose = useCallback(() => {
        resetState();
        onClose?.();
    }, [onClose, resetState]);

    useEffect(() => {
        if (!open) return;
        resetState();
    }, [open, resetState]);

    const handleDisable = async () => {
        if (code.length < CODE_LENGTH) return;
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${basePath}/auth/2fa/disable`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ code: code.trim() }),
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data?.message || "Failed to disable 2FA");
            }
            handleClose();
            onDisabled?.();
        } catch (err) {
            setError(err.message || "Failed to disable 2FA");
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    const declineButtonSx = {
        ...LOBBY_EXIT_BUTTON_SX,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.4rem",
        minHeight: "2.5rem",
        px: "1.15rem",
        fontSize: "0.82rem",
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "none",
        flex: 1,
        minWidth: 0,
    };

    const cancelButtonSx = {
        ...DIALOG_CANCEL_BUTTON_SX,
        minHeight: "2.35rem",
        fontSize: "0.78rem",
        letterSpacing: "0.04em",
        textTransform: "none",
        flex: 1,
    };

    return (
        <BackdropOverlay open onClick={handleClose}>
            <Box
                role="dialog"
                aria-modal="true"
                aria-labelledby="two-factor-disable-title"
                sx={DIALOG_PAPER_SX}
            >
                <IconButton
                    onClick={handleClose}
                    aria-label="Close disable two-factor dialog"
                    sx={{
                        position: "absolute",
                        top: "0.8rem",
                        right: "0.8rem",
                        width: "1.75rem",
                        height: "1.75rem",
                        borderRadius: "0.55rem",
                        color: colors.text.secondary,
                        backgroundColor: "rgba(255, 255, 255, 0.04)",
                        "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.08)",
                        },
                    }}
                >
                    <Close sx={{ fontSize: "0.95rem" }} />
                </IconButton>

                <Box sx={{ px: "1.85rem", pb: "1.65rem", pt: "1.5rem" }}>
                    <Box
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: "14px",
                            mb: "0.85rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: alpha(colors.state.danger.main, 0.12),
                            border: `1px solid ${alpha(colors.state.danger.main, 0.28)}`,
                            color: colors.state.danger.main,
                        }}
                    >
                        <GppBadOutlined sx={{ fontSize: "1.5rem" }} />
                    </Box>

                    <Typography
                        id="two-factor-disable-title"
                        sx={{
                            color: colors.text.primary,
                            fontSize: "1.2rem",
                            fontWeight: 700,
                            lineHeight: 1.25,
                        }}
                    >
                        Disable two-factor authentication
                    </Typography>
                    <Typography
                        sx={{
                            color: colors.text.secondary,
                            fontSize: "0.85rem",
                            lineHeight: 1.5,
                            mt: "0.4rem",
                        }}
                    >
                        Enter the current 6-digit code from your authenticator app to turn off
                        two-factor authentication.
                    </Typography>

                    <SixDigitCodeInput
                        value={code}
                        onChange={setCode}
                        disabled={loading}
                        autoFocus
                    />

                    {error ? (
                        <Typography
                            role="alert"
                            sx={{
                                mt: "0.85rem",
                                color: colors.state.danger.main,
                                fontSize: "0.78rem",
                                lineHeight: 1.45,
                                textAlign: "center",
                            }}
                        >
                            {error}
                        </Typography>
                    ) : null}

                    <Box
                        sx={{
                            display: "flex",
                            gap: "0.65rem",
                            mt: "1.35rem",
                        }}
                    >
                        <Button onClick={handleClose} disabled={loading} sx={cancelButtonSx}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDisable}
                            disabled={loading || code.length < CODE_LENGTH}
                            sx={declineButtonSx}
                        >
                            {loading ? (
                                <CircularProgress size={16} sx={{ color: "inherit" }} />
                            ) : (
                                <GppBadOutlined sx={{ fontSize: "1rem" }} />
                            )}
                            Disable 2FA
                        </Button>
                    </Box>
                </Box>
            </Box>
        </BackdropOverlay>
    );
}

TwoFactorAuthDisableDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onDisabled: PropTypes.func,
};
